import { useRef, useEffect, useCallback } from "react"

type UseDialogOptions = {
  isOpen: boolean
  onClose: () => void
}

/**
 * Custom hook for managing native HTML dialog element state.
 *
 * Provides:
 * - A ref to attach to the dialog element
 * - Automatic open/close handling based on `isOpen` prop
 * - Native dialog close event handling (e.g., ESC key)
 * - Backdrop click handling to close the dialog
 * - Event propagation prevention (useful when dialog is inside a Sheet/Modal)
 *
 * @example
 * ```tsx
 * function MyDialog({ isOpen, onClose }) {
 *   const { dialogRef, handleBackdropClick, stopPropagation } = useDialog({
 *     isOpen,
 *     onClose,
 *   })
 *
 *   if (!isOpen) return null
 *
 *   return (
 *     <Dialog
 *       ref={dialogRef}
 *       onClick={handleBackdropClick}
 *       onPointerDown={stopPropagation}
 *       onMouseDown={stopPropagation}
 *     >
 *       <DialogContent>...</DialogContent>
 *     </Dialog>
 *   )
 * }
 * ```
 */
export function useDialog({ isOpen, onClose }: UseDialogOptions) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  // Handle dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [isOpen])

  // Handle native dialog close event (e.g. ESC key)
  // Stop propagation to prevent closing parent components (e.g., sidepanel)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleCloseEvent = (event: Event) => {
      event.stopPropagation()
      onClose()
    }

    dialog.addEventListener("close", handleCloseEvent)
    return () => dialog.removeEventListener("close", handleCloseEvent)
  }, [onClose])

  // Handle backdrop click to close modal
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      // Close only if clicking on the dialog backdrop (not the content)
      if (event.target === dialogRef.current) {
        event.stopPropagation()
        dialogRef.current?.close()
      }
    },
    [],
  )

  // Stop pointer events from propagating to prevent parent components from closing
  // (e.g., Radix UI Sheet uses pointerdown on overlay to trigger close)
  const stopPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
  }, [])

  // Programmatic close function
  const close = useCallback(() => {
    dialogRef.current?.close()
  }, [])

  return {
    dialogRef,
    handleBackdropClick,
    stopPropagation,
    close,
  }
}
